import dayjs, { ConfigType, Dayjs, ManipulateType, QUnitType } from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import calendar from 'dayjs/plugin/calendar'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import localeData from 'dayjs/plugin/localeData'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import minMax from 'dayjs/plugin/minMax'
import objectSupport from 'dayjs/plugin/objectSupport'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import weekday from 'dayjs/plugin/weekday'
import weekOfYear from 'dayjs/plugin/weekOfYear'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(localizedFormat)
dayjs.extend(relativeTime)
dayjs.extend(calendar)
dayjs.extend(isSameOrAfter)
dayjs.extend(quarterOfYear)
dayjs.extend(minMax)
dayjs.extend(objectSupport)
dayjs.extend(localeData)
dayjs.extend(weekday)
dayjs.extend(weekOfYear)

dayjs.tz.setDefault('UTC')

export type { ConfigType, Dayjs }

export type DateUnit = Exclude<ManipulateType | QUnitType, 'date' | 'dates'>

// quarterOfYear types add/subtract as a separate QUnitType overload, so a unit
// that may be a quarter or a week matches neither overload.
declare module 'dayjs' {
    interface Dayjs {
        add(value: number, unit: DateUnit): Dayjs
        subtract(value: number, unit: DateUnit): Dayjs
    }
}

// The app runs in UTC, but a bare dayjs() is local time and dayjs has no global
// default for it. Construction therefore goes through this factory only; the raw
// dayjs default export must never leave this module (ESLint enforces the import side).
// An existing Dayjs passes through unchanged, keeping any zone set with .tz();
// dayjs.utc(d) would re-zone it to UTC.
export const date = Object.assign(
    (input?: ConfigType, format?: string, strict?: boolean): Dayjs =>
        dayjs.isDayjs(input) ? input : dayjs.utc(input, format, strict),
    {
        tz: dayjs.tz,
        isDayjs: dayjs.isDayjs,
        min: dayjs.min,
        max: dayjs.max,
        locale: dayjs.locale,
        weekdays: dayjs.weekdays,
    }
)
